<script setup>
import { ref, computed, onMounted } from 'vue'
import { useQuasar } from 'quasar'
import dayjs from 'dayjs'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'
import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useShare } from '@/composables/share'
import { useDetectDevice } from '@/composables/device'
import { usePdfPreview } from '@/composables/pdf-preview'
import { usePrintSettings, useReportColumnTemplates } from '@/composables/report-column-template'
import { useWebSiteStore } from '@/stores/web-site.store'

import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import PdfPreviewPanel from '@/components/shared/PdfPreviewPanel.vue'
import PrintDialogLayout from '@/components/shared/PrintDialogLayout.vue'
import PrintConfigurator from '@/components/accounting/PrintConfigurator.vue'
import PrintContentEditor from '@/components/accounting/PrintContentEditor.vue'

const props = defineProps({
  budgetDocumentId: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['cancel'])

const i18n = getI18n()
const { step, nextStep, prevStep } = useStepper()
const { notifySuccess } = useNotify()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const share = useShare()
const $q = useQuasar()
const { isMobile } = useDetectDevice()

const isLoading = ref(false)
const budgetDocument = ref(null)
const magicLinkUrl = ref(null)
const magicLinkCreatedAt = ref(null)
const config = ref(null)
const templateId = ref(null)
const printTemplateId = ref(null)
const language = ref(null)
const { documentOptions } = useReportColumnTemplates()
const settings = usePrintSettings({ config, templateId, printTemplateId, defaultPrintTemplateId: webSiteStore.getDefaultQuotePrintTemplate() })
const groupRowsBy = computed(() => config.value.group_by)
const { blobUrl: previewBlobUrl, source: previewPdfSource, isLoading: isPreviewLoading, isPrinting, refreshAsync: refreshPreview, printAsync } = usePdfPreview(
  () => accountingStore.previewBudgetDocumentAsync(props.budgetDocumentId)
)

const fetchMagicLink = async () => {
  magicLinkUrl.value = null
  magicLinkCreatedAt.value = null
  const res = (await accountingStore.getBudgetDocumentMagicLinkAsync(props.budgetDocumentId))?.data
  if (res.created_at) {
    magicLinkUrl.value = res?.url || null
    magicLinkCreatedAt.value = res?.created_at ? dayjs(res.created_at) : null
  }
}

const fetchBudgetDocumentAsync = async () => {
  budgetDocument.value = (await accountingStore.api.getBudgetDocumentByIdAsync(props.budgetDocumentId)).data
  printTemplateId.value = budgetDocument.value.print_template
  language.value = budgetDocument.value.language || portalStore.selectedLanguage
  settings.restore(accountingStore.printSettingTypeEnum.QUOTE, budgetDocument.value.output_config, budgetDocument.value.column_template)
  if (!budgetDocument.value.output_config?.sections) config.value.group_by = budgetDocument.value.group_rows_by
}

const saveAsync = async (data) => {
  await accountingStore.upsertBudgetDocumentAsync({ id: props.budgetDocumentId, ...data })
  refreshPreview()
}
const saveConfigAsync = () => saveAsync({ output_config: config.value, column_template: templateId.value })
const onConfigChanged = (value) => {
  config.value = value
  saveConfigAsync()
}
const onConfigOptionChanged = (key, value) => onConfigChanged({ ...config.value, [key]: value })
const onSettingsApplied = () => saveAsync({ output_config: config.value, column_template: templateId.value, print_template: printTemplateId.value })

const onPrintTemplateChange = (id) => {
  printTemplateId.value = id
  saveAsync({ print_template: id })
}

const onLanguageChange = (value) => {
  language.value = value
  saveAsync({ language: value })
}

onMounted(async () => {
  fetchMagicLink()
  await Promise.all([accountingStore.fetchReportColumnsAsync(), accountingStore.fetchReportColumnTemplatesAsync()])
  await fetchBudgetDocumentAsync()
  refreshPreview()
})


const shareLinkAsync = async (url) => share.shareLinkAsync(url, i18n.t('common.budgetDocument'), () => notifySuccess(i18n.t('common.magicLinkCopiedToClipboard'), {
  actions: [
    { label: i18n.t('common.ok'), color: 'white', handler: () => {} },
    { label: i18n.t('common.open'), color: 'white', bold: true, iconRight: 'mdi-open-in-new', handler: () => { window.open(url, '_blank') } },
  ],
  timeout: 0,
}))

const groupByName = computed(() => i18n.t(accountingStore.budgetDocumentGroupRowsByLabels[groupRowsBy.value]))

const onGroupByInfoClick = () => {
  const { DAY, PRIMARY_TAG } = accountingStore.budgetDocumentGroupRowsByEnum
  const title = groupByName.value
  const message = groupRowsBy.value === DAY ? i18n.t('info.groupByDayInfo')
    : groupRowsBy.value === PRIMARY_TAG ? i18n.t('info.groupByPrimaryTagsInfo')
    : i18n.t('info.groupByNoneInfo')
  $q.dialog({ title, message, html: true, ok: { flat: true, label: i18n.t('common.close'), color: 'primary' } })
}

const onGroupRowsByUpdated = (value) => {
  const { DAY, NONE, PRIMARY_TAG } = accountingStore.budgetDocumentGroupRowsByEnum
  onConfigOptionChanged('group_by', value === true ? DAY : value === false ? NONE : PRIMARY_TAG)
}

const contentOptions = computed(() => documentOptions(config.value))

const onPrintAsync = async () => {
  if (!await printAsync(`${budgetDocument.value.name} - ${i18n.t('common.budgetDocument')}.pdf`, budgetDocument.value.name)) return
  notifySuccess(i18n.t('common.documentPrinted'))
  emit('cancel')
}

const onExportToExcel = async () => {
  const templates = accountingStore.getLocalizedBudgetDocumentExportTemplates()
  if (!templates.length) return
  isLoading.value = true
  try {
    await accountingStore.exportBudgetDocumentToExcelAsync(
      props.budgetDocumentId,
      `${budgetDocument.value.name} - ${i18n.t('common.budgetDocument')}.xlsx`,
      {
        template_id: templates[0].id,
        group_by_primary_budget_tags: groupRowsBy.value === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG,
      }
    )
    notifySuccess(i18n.t('common.documentExported'))
    emit('cancel')
  } finally {
    isLoading.value = false
  }
}

const onGetMagicLink = async () => {
  isLoading.value = true
  try {
    const url = (await accountingStore.printBudgetDocumentMagicLinkAsync(props.budgetDocumentId)).data?.url
    magicLinkUrl.value = url
    await shareLinkAsync(url)
    emit('cancel')
  } finally {
    isLoading.value = false
  }
}

const onSubstituteAndUpdateMagicLink = onGetMagicLink

const onNextStep = () => nextStep()

const hasMagicLink = computed(() => !!magicLinkUrl.value)

const magicLinkDescription = computed(() => (hasMagicLink.value ? i18n.t('info.magicLinkAlreadyExisting', { date: magicLinkCreatedAt.value ? dayjs(magicLinkCreatedAt.value).format('MMMM D YYYY [at] HH:mm') : '' }) : i18n.t('info.magicLinkDescription')))

</script>
<template>
  <print-dialog-layout
    :title="i18n.t('common.print') + ' - ' + i18n.t('common.budgetDocument')"
    :source="previewPdfSource"
    :loading="isPreviewLoading"
    :printable="!!previewBlobUrl"
    :printing="isPrinting"
    @cancel="emit('cancel')"
    @print="onPrintAsync"
  >
    <template #options>
      <div v-if="budgetDocument && config" class="q-pa-md">
        <q-stepper
          v-model="step"
          vertical
          class="q-py-none shadow-0"
          style="background: transparent;"
        >
          <q-step
            :name="1"
            :title="i18n.t('common.report.steps.content')"
            :caption="settings.caption.value"
            icon="mdi-file-document-outline"
            :done="step > 1"
          >
            <print-content-editor
              :model-value="config"
              :settings="settings"
              :options="contentOptions"
              :type-name="i18n.t('common.budgetDocument')"
              @update:model-value="onConfigChanged"
              @update:option="onConfigOptionChanged"
              @applied="onSettingsApplied"
            >
              <div class="row items-center">
                <q-toggle
                  toggle-indeterminate
                  keep-color
                  :class="{ 'group-by-day-toggle': groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.DAY, 'group-by-primary-tag-toggle': groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG, 'ungrouped-toggle': groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.NONE }"
                  :icon="groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.DAY ? 'mdi-calendar' : groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG ? 'mdi-tag-multiple-outline' : 'mdi-view-list-outline'"
                  :model-value="groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.DAY ? true : groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.PRIMARY_TAG ? null : false"
                  :label="groupByName"
                  @update:model-value="onGroupRowsByUpdated"
                />
                <q-btn
                  round
                  flat
                  dense
                  icon="mdi-information-outline"
                  size="sm"
                  class="q-ml-xs"
                  @click="onGroupByInfoClick"
                />
              </div>
            </print-content-editor>
            <stepper-navigation class="q-mt-md" :continue="onNextStep" />
          </q-step>

          <q-step
            :name="2"
            :title="i18n.t('common.printConfigurator')"
            icon="mdi-printer-settings"
            :done="step > 2"
          >
            <print-configurator
              class="q-mt-sm"
              :document-type="accountingStore.printDocumentTypeEnum.QUOTE"
              :print-template="printTemplateId"
              :language="language"
              @update:print-template="onPrintTemplateChange"
              @update:language="onLanguageChange"
            />
            <div v-if="isMobile" class="q-mt-sm" style="height: 60vh;">
              <pdf-preview-panel :source="previewPdfSource" :loading="isPreviewLoading" />
            </div>
            <stepper-navigation
              class="q-mt-md"
              :loading="isLoading"
              :back="prevStep"
              :continue="onNextStep"
            />
          </q-step>

          <q-step
            :name="3"
            :title="i18n.t('common.magicLink')"
            icon="mdi-link-variant"
            :done="step > 3"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="q-mt-sm text-body2" v-html="magicLinkDescription" />
            <stepper-navigation
              class="q-mt-md"
              color="primary"
              icon="mdi-link-variant"
              :loading="isLoading"
              :back="prevStep"
              :finish="onGetMagicLink"
              :finish-label="i18n.t('common.getMagicLink')"
            >
              <q-btn
                v-if="hasMagicLink"
                color="warning"
                icon="mdi-link-variant-plus"
                :label="i18n.t('common.update')"
                :loading="isLoading"
                @click="onSubstituteAndUpdateMagicLink"
              />
            </stepper-navigation>
          </q-step>
        </q-stepper>
      </div>
    </template>
    <template #actions>
      <q-btn
        class="large-btn"
        color="positive"
        icon="mdi-microsoft-excel"
        :label="i18n.t('common.exportToExcel')"
        :loading="isLoading"
        @click="onExportToExcel"
      />
    </template>
  </print-dialog-layout>
</template>
<style scoped lang="scss">
:deep(.group-by-day-toggle) {
  cursor: pointer;
  & .q-toggle__thumb:after {
    background-color: $blue !important;
  }
  & .q-toggle__track {
    background-color: $blue !important;
    color: white !important;
  }
}

:deep(.group-by-primary-tag-toggle) {
  cursor: pointer;
  & .q-toggle__thumb:after {
    background-color: $positive !important;
  }
  & .q-toggle__track {
    background-color: $positive !important;
    color: white !important;
  }
}

:deep(.ungrouped-toggle) {
  cursor: pointer;
  & .q-toggle__thumb:after {
    background-color: $grey !important;
  }
  & .q-toggle__track {
    background-color: $grey !important;
    color: white !important;
  }
}
</style>
